import store from '@/store'
import { computed } from 'vue'
import { get } from 'lodash'
import { useShop } from '@/use/shop'

/**
 * 檢查 org 層級「組織通用設定」功能開關。
 * 當 enableOrgBenefitSharing 為 false 時，整體組織通用權益功能視為關閉。
 */
const isOrgBenefitSharingFeatureEnabled = () => {
  return get(store.state, 'org.org.DashboardConfig.orgModulePermission.enableOrgBenefitSharing', false)
}

export const isBenefitSharingEnabled = (type, currentShopId = store.getters.shop) => {
  if (!isOrgBenefitSharingFeatureEnabled()) return false

  const configKey = type === 'point' ? 'points' : type
  const benefitTypeConfig = get(store.state, `shop.shopSetting.benefitSharing.${configKey}`) || {}
  const sourceShops = get(benefitTypeConfig, 'sourceShops') || []

  return get(benefitTypeConfig, 'enabled') ||
    get(benefitTypeConfig, 'sharingEnabled') ||
    get(benefitTypeConfig, 'mode') === 'readonly' ||
    sourceShops.some(s => s.id === currentShopId)
}

/**
 * 將舊的 crossShop 參數映射為新的 benefitShopScope。
 * @param {Object} params - 轉換參數
 * @param {'local'|'all'|'shared'} [params.benefitShopScope] - 新版查詢範圍
 * @param {boolean} [params.crossShop] - 舊版跨店參數
 * @param {boolean} [params.fallbackCrossShop] - 沒有明確傳值時的舊行為回退
 * @returns {'local'|'all'|'shared'|undefined} 轉換後的查詢範圍
 */
export const resolveBenefitShopScope = ({ benefitShopScope, crossShop, fallbackCrossShop } = {}) => {
  if (benefitShopScope) return benefitShopScope
  if (crossShop === true || fallbackCrossShop === true) return 'all'
  if (crossShop === false || fallbackCrossShop === false) return 'local'

  return undefined
}

/**
 * 讀取當前分店的組織通用權益控制模式。
 * 資料來源：store.state.shop.shopSetting.benefitSharing（由 GetShopSetting 全域注入）。
 *
 * 當 org 層級的 enableOrgBenefitSharing 關閉時，所有組織通用權益狀態均視為未啟用。
 *
 * mode === 'readonly' 表示組織已接管該類型，分店無法新增/編輯/刪除。
 * sourceShops 中包含 currentShopId 表示目前分店為該類型的來源店。
 */
export const useOrgBenefitControl = () => {
  const { isHeadquarter } = useShop()

  const enableOrgBenefitSharing = computed(() => isOrgBenefitSharingFeatureEnabled())

  const benefitSharing = computed(() => {
    if (!enableOrgBenefitSharing.value) return {}
    return get(store.state, 'shop.shopSetting.benefitSharing') || {}
  })

  const isClassTicketOrgEnabled = computed(() => get(benefitSharing.value, 'classTicket.enabled'))
  const isClassTicketReadOnly = computed(() => isClassTicketOrgEnabled.value && get(benefitSharing.value, 'classTicket.mode') === 'readonly')
  const isClassTicketMixed = computed(() => isClassTicketOrgEnabled.value && get(benefitSharing.value, 'classTicket.mode') === 'mixed')
  const isClassTicketBenefitSharingEnabled = computed(() => isBenefitSharingEnabled('classTicket'))
  const isCouponReadOnly = computed(() => get(benefitSharing.value, 'coupon.mode') === 'readonly')
  const isCouponMixed = computed(() => get(benefitSharing.value, 'coupon.mode') === 'mixed')
  const isCouponBenefitSharingEnabled = computed(() => isBenefitSharingEnabled('coupon'))
  const isPointReadOnly = computed(() => get(benefitSharing.value, 'points.mode') === 'readonly')
  const isPointMixed = computed(() => get(benefitSharing.value, 'points.mode') === 'mixed')
  const isMemberLevelReadOnly = computed(() => get(benefitSharing.value, 'memberLevel.mode') === 'readonly')
  const isWalletReadOnly = computed(() => get(benefitSharing.value, 'wallet.mode') === 'readonly')
  const isWalletBenefitSharingEnabled = computed(() => isBenefitSharingEnabled('wallet'))
  const isCashbackReadOnly = computed(() => get(benefitSharing.value, 'cashback.mode') === 'readonly')
  const isCashbackBenefitSharingEnabled = computed(() => isBenefitSharingEnabled('cashback'))
  const isPointBenefitSharingEnabled = computed(() => isBenefitSharingEnabled('point'))

  return {
    enableOrgBenefitSharing,
    benefitSharing,
    isHeadquarter,
    isClassTicketOrgEnabled,
    isClassTicketReadOnly,
    isClassTicketMixed,
    isClassTicketBenefitSharingEnabled,
    isCouponReadOnly,
    isCouponMixed,
    isCouponBenefitSharingEnabled,
    isPointReadOnly,
    isPointMixed,
    isMemberLevelReadOnly,
    isWalletReadOnly,
    isWalletBenefitSharingEnabled,
    isCashbackReadOnly,
    isCashbackBenefitSharingEnabled,
    isPointBenefitSharingEnabled,
  }
}
